import { Injectable } from '@angular/core';
import { Confirmation, ConfirmationService, MessageService } from 'primeng/api';
import { BehaviorSubject } from 'rxjs';
import { Tenant } from 'src/app/models/tenant';
import { AlertService, MessageSeverity } from 'src/app/services/alert.service';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { PermissionDictionary, UserPermissionService } from 'src/app/services/user/user-permission.service';

@Injectable({
    providedIn: 'root'
})
export class TenantUIService {

    selectedEntityIndex: number;
    selectedTenant: Tenant;

    private _currentTenantConfiguration$ = new BehaviorSubject<any>(null);
    currentTenantConfiguration$ = this._currentTenantConfiguration$.asObservable();
    get currentTenantConfiguration() {
        return this._currentTenantConfiguration$.value;
    }

    processingTenant: boolean;

    get isSettingsCurrentEntity() {
        return this.selectedEntityIndex == 0 && this.userPermissionService.hasPermission(PermissionDictionary.Tenant_SettingsEditor);
    }
    get isProductsCurrentEntity() {
        return this.selectedEntityIndex == this.ProductsIndex;
    }
    get isCampaignCurrentEntity() {
        return this.selectedEntityIndex == this.CampaignsIndex;
    }
    get isIngestionCurrentEntity() {
        return this.selectedEntityIndex == this.IngestionIndex;
    }


    constructor(
        private readonly userPermissionService: UserPermissionService,
        private readonly confirmationService: ConfirmationService,
        private readonly alertService: AlertService,
        private readonly tenantService: TenantService
    ) { }

    async setTenantName(tenantName: string) {
        const tenants = await this.tenantService.allTenants$.toPromise();
        this.selectedTenant = tenants.find(item => item.tenantName === tenantName);
        this.selectedSettingsEditor();
    }


    get SettingsEditorIndex() {
        return 0;
    }

    get ProductsIndex() {
        if (!this.userPermissionService.hasPermission(PermissionDictionary.Tenant_SettingsEditor)) {
            return 0;
        } else {
            return 1;
        }
    }

    get CampaignsIndex() {
        if (!this.userPermissionService.hasPermission(PermissionDictionary.Tenant_SettingsEditor)) {
            return 1;
        } else {
            return 2;
        }
    }

    get IngestionIndex() {
        if (!this.userPermissionService.hasPermission(PermissionDictionary.Tenant_SettingsEditor)) {
            return 2;
        } else {
            return 3;
        }
    }

    get TenantMCMDApprovalViewPermName() {
        const tenantName = this.selectedTenant?.tenantName;
        return "UI.Tenants." + tenantName + ".Ingestion.MCMD.Approval.View";
    }

    get TenantMCMDApprovalEditPermName() {
        const tenantName = this.selectedTenant?.tenantName;
        return "UI.Tenants." + tenantName + ".Ingestion.MCMD.Approval.Edit";
    }

    get userHasIngestionPermissions() {
        return this.userPermissionService.hasPermission(this.TenantMCMDApprovalEditPermName) ||
               this.userPermissionService.hasPermission(this.TenantMCMDApprovalViewPermName);
    }

    get userHasIngestionApprovalEditPermissions() {
        return this.userPermissionService.hasPermission(this.TenantMCMDApprovalEditPermName);
    }

    selectEntity(entityIndex: number)
    selectEntity(entity: string)
    async selectEntity(entityId: any) {
        this.clearEntitySelection();
        if (typeof entityId == 'string') {
            const entity = entityId;
            switch (entity?.toUpperCase()) {
                case 'SETTINGS_EDITOR': {
                    this.selectedEntityIndex = this.SettingsEditorIndex;
                    this.selectedSettingsEditor();
                    break;
                }
                case 'PRODUCTS': {
                    this.selectedEntityIndex = this.ProductsIndex;
                    break;
                }
                case 'CAMPAIGNS': {
                    this.selectedEntityIndex = this.CampaignsIndex;
                    break;
                }
                case 'INGESTION': {
                    this.selectedEntityIndex = this.IngestionIndex;
                    break;
                }
            }
        }
        else {
            this.selectedEntityIndex = entityId as number;
        }
    }
    async selectedSettingsEditor() {
        try {
            this.processingTenant = true;
            this._currentTenantConfiguration$.next(null);
            this._currentTenantConfiguration$.next(await this.tenantService.getConfiguration(this.selectedTenant.tenantName));
        } finally {
            this.processingTenant = false;
        }
    }

    async saveChangesConfirmation(fn: () => Promise<void>, confirmation?: Confirmation) {
        confirmation = {
                key: 'tenant-manager-confirmation',
                message: `Please confirm the changes? After accept, the changes will be applied.`,
                icon: 'fal fa-exclamation-triangle',
                ...confirmation
        };

        this.confirmationService.confirm({
            ...confirmation,
            accept: async () => {
                await fn();
            },
            reject: () => {

            }
        });
    }

    async saveTenantConfiguration(data: any) {
        try {
            this.processingTenant = true;
            const updatedConfigTenantDto = await this.tenantService.saveConfiguration(this.selectedTenant.tenantName, data);
            this._currentTenantConfiguration$.next(updatedConfigTenantDto.configuration);
            this.alertService.showStickyMessage('Tenant configuration updated', `Tenant configuration for ${this.selectedTenant.tenantName}(${this.selectedTenant.displayName}) was success`, MessageSeverity.success);
        } finally {
            this.processingTenant = false;
        }
    }

    private clearEntitySelection() {
        this._currentTenantConfiguration$.next(null);
    }


}
